import { useEffect, useRef } from 'react'
import { Badge, Button, Empty, Spin } from 'antd'
import styled from 'styled-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'

import { sectionRenderer } from './layouts'
import * as CmsSectionService from '../../../../services/bb-api/oplevalt/cms/section'
import { txt } from '../../../../locales/i18n'

const getNextElement = (container: HTMLElement, clientY: number) => {
	const elements = [...container.querySelectorAll('section:not(.dragging)')]

	type Target = { offset: number; element: Element | null }
	const initialTarget: Target = { offset: Number.NEGATIVE_INFINITY, element: null }

	const target = elements.reduce((target, element) => {
		const { top, height } = element.getBoundingClientRect()
		const offset = (clientY - top - height) / 2

		if (offset > 0) return target
		if (offset < target.offset) return target

		return { offset, element }
	}, initialTarget)

	return target.element
}

export type CmsPreviewContainerProps = {
	sections: any[]
	onSectionSelection: (sectionId: number) => void
	pageId: string
	selectedSectionId: number | null
	previewType: 'desktop' | 'mobile'
	isLoadingSections: boolean
	openLayoutDrawer: () => void
}

export const CmsPreviewContainer = (props: CmsPreviewContainerProps) => {
	const previewContainerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const previewContainer = previewContainerRef.current
		if (previewContainer === null) return

		const handleDragOver = (event: DragEvent) => {
			event.preventDefault()
			const target = document.querySelector('.dragging')

			if (target === null) return

			const previewZoom = 0.5
			const nextElement = getNextElement(previewContainer, event.clientY / previewZoom)

			if (nextElement === null) {
				return previewContainer.appendChild(target)
			}

			previewContainer.insertBefore(target, nextElement)
		}

		const handleDragStart = (element: Element) => {
			element.classList.add('dragging')
		}

		const handleDragEnd = async (element: Element) => {
			element.classList.remove('dragging')

			const previewContainer = previewContainerRef.current
			if (previewContainer === null) return

			const sectionIds = [...previewContainer.children].map(({ id }) => id)
			await CmsSectionService.updateOrder(props.pageId, sectionIds)
		}

		previewContainer.addEventListener('dragover', handleDragOver)
		const elements = [...previewContainer.children]

		elements.forEach((element) => {
			element.addEventListener('dragstart', () => handleDragStart(element))
			element.addEventListener('dragend', () => handleDragEnd(element))
		})

		return () => {
			previewContainer.removeEventListener('dragover', handleDragOver)

			elements.forEach((element) => {
				element.removeEventListener('dragstart', () => handleDragStart(element))
				element.removeEventListener('dragend', () => handleDragEnd(element))
			})
		}
	}, [props.pageId, props.sections])

	const getClassName = (section: any) => {
		const classNames = ['preview-container']

		if (section.id === props.selectedSectionId) {
			classNames.push('selected')
		}

		if (section?.content?.published === false) {
			classNames.push('unreleased')
		}

		return classNames.join(' ')
	}

	const handleSelectSection = (event: React.MouseEvent, sectionId: number) => {
		event.stopPropagation()
		props.onSectionSelection(sectionId)
	}

	return (
		<CmsPreviewContainerStyled ref={previewContainerRef} previewType={props.previewType}>
			{props.isLoadingSections && <Spin size="large" />}

			{props.isLoadingSections === false && props.sections.length === 0 && (
				<Empty description={null}>
					<Button type="primary" onClick={props.openLayoutDrawer}>
						{txt('cms.add layout')}
					</Button>
				</Empty>
			)}

			{props.sections.map((section: any) => {
				const renderBadgeContent = () => {
					if (section.unsaved) {
						return <FontAwesomeIcon size="2x" color="#FFAA33" icon={faCircleExclamation} />
					}

					return null
				}

				// SortableWrapper
				return (
					<section
						className={getClassName(section)}
						onClick={(event) => handleSelectSection(event, section.id)}
						key={section.id}
						id={section.id}
						draggable
					>
						<Badge offset={[-6, 6]} count={renderBadgeContent()}>
							{sectionRenderer(section.layoutName, section.content)}
						</Badge>
					</section>
				)
			})}
		</CmsPreviewContainerStyled>
	)
}

const CmsPreviewContainerStyled = styled.div<{
	previewType: CmsPreviewContainerProps['previewType']
}>`
	position: relative;
	zoom: 0.5;
	display: flex;
	flex-direction: column;
	gap: 1ch;

	.dragging {
		opacity: 0.2;
	}

	.ant-spin {
		margin: 2ch;
	}

	.ant-empty {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		zoom: 2;
	}

	.preview-container {
		cursor: pointer;
		padding-inline: 2ch;
		border-left: 6px solid transparent;

		-webkit-user-select: none; /* Safari */
		-ms-user-select: none; /* IE 10 and IE 11 */
		user-select: none; /* Standard syntax */

		transition: transform 100ms ease-in;

		&:hover {
			transform: scale(1.01);
		}

		span > div {
			width: ${({ previewType }) => {
				return previewType === 'mobile' ? 'calc(100dvh / 16 * 9)' : '100dvw'
			}};

			overflow: hidden;
			border-radius: 1ch;
			box-shadow: 0 0 1rem #0001;

			&:hover {
				box-shadow: 0 0 2ch #aaa;
			}
		}
	}

	.preview-container.selected {
		border-left: 6px solid dodgerblue;
	}

	.preview-container.unreleased {
		border-right: 6px dashed #cdcdcd;
	}

	h1,
	h2,
	h3,
	h4,
	p {
		font-size: 1rem;
		margin: 0;
		padding: 0;
		white-space: pre-wrap;
	}

	h1 {
		// letter-spacing: ;
		// line-height: ;
		font-size: 2rem;
	}

	h2 {
		// letter-spacing: ;
		// line-height: ;
		font-size: 1.8rem;
	}

	h3 {
		// letter-spacing: ;
		// line-height: ;
		font-size: 1.4rem;
	}

	h4 {
		// letter-spacing: ;
		// line-height: ;
		font-size: 1.2rem;
	}

	p {
		// letter-spacing: ;
		// line-height: ;
		font-size: 1rem;
	}
`
